// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ExpandLess, ExpandMore } from '@/constants/hardfin-standard-icons';
import { InventoryEntry, getTotalCount } from '@/inventory/api/types';
import { InventoryDimension } from '@/inventory/hooks';
import { toZoneName } from '@/inventory/utilities/to-zone-name';
import { InventoryChildRowActions } from '@/items/components/inventory-child-row-actions';
import { InventoryLotDetails } from '@/items/components/inventory-lot-details';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, ReactNode } from 'react';

interface GroupedItemInventoryItem {
  item: SparseItem;
  locations: {
    location: SparseLocation;
    inventory: InventoryEntry[];
  }[];
}

interface GroupedLocationInventoryItem {
  location: SparseLocation;
  items: {
    bulkItems: {
      item: SparseItem;
      location: SparseLocation;
      inventory: InventoryEntry[];
    }[];
  };
}

interface InventoryChildRowProps {
  view: InventoryDimension;
  expanded: boolean;
  locationRow?: GroupedLocationInventoryItem;
  itemRow?: GroupedItemInventoryItem;
  expandedZones: Record<string, boolean>;
  onToggleZoneExpand: (zoneKey: string) => void;
  expandedLots: Record<string, boolean>;
  onToggleLotExpand: (lotKey: string) => void;
}

// Zone and lot expand keys share the same key space (item-location pairs
// are unique within their respective Record), so a single key function works for both.
function getLotKey(itemKey: string, locationKey: string): string {
  return `${itemKey}-${locationKey}`;
}

/**
 * Returns whether a site's stock needs the zone breakdown UI
 */
function hasZoneBreakdown(
  site: SparseLocation,
  inventory: InventoryEntry[]
): boolean {
  return (
    site.zoneCount > 0 ||
    inventory.some((entry) => entry.location.id !== site.id)
  );
}

/**
 * Renders the expand toggle for a row's lots or zones
 */
function ExpandToggle(props: {
  isExpanded: boolean;
  noun: 'lots' | 'zones';
  onToggle: () => void;
}) {
  const detail = props.noun === 'lots' ? 'lot details' : 'zone details';

  return (
    <Tooltip
      arrow
      title={props.isExpanded ? `Hide ${props.noun}` : `Show ${props.noun}`}
    >
      <IconButton
        size="small"
        onClick={props.onToggle}
        aria-label={
          props.isExpanded ? `Collapse ${detail}` : `Expand ${detail}`
        }
      >
        {props.isExpanded ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
}

/**
 * Renders one location's stock under its site, with expand/collapse for lot details
 */
function ZoneSubRow(props: {
  entry: InventoryEntry;
  site: SparseLocation;
  item: SparseItem;
  expandedLots: Record<string, boolean>;
  onToggleLotExpand: (lotKey: string) => void;
}) {
  const lotKey = getLotKey(props.item.key, props.entry.location.key);
  // The site's own floor stays usable on an archived site, as only a zone is archived on its own.
  const isArchived =
    props.entry.location.id !== props.site.id &&
    props.entry.location.isArchived;
  const isLotExpanded = !isArchived && (props.expandedLots[lotKey] ?? false);
  const zoneName = toZoneName(props.entry, props.site.id);

  return (
    <Fragment>
      <TableRow>
        <TableCell width="55%" sx={{ paddingLeft: 10 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isArchived ? (
              <Box sx={{ width: 28 }} />
            ) : (
              <ExpandToggle
                isExpanded={isLotExpanded}
                noun="lots"
                onToggle={() => props.onToggleLotExpand(lotKey)}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {zoneName}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell width="20%" align="right">
          <Typography variant="body2">
            {formatNumber(props.entry.count)}
          </Typography>
        </TableCell>
        <TableCell width="25%" align="right">
          {!isArchived && (
            <InventoryChildRowActions
              item={props.item}
              location={props.entry.location}
              quantity={props.entry.count}
            />
          )}
        </TableCell>
      </TableRow>
      {!isArchived && (
        <InventoryLotDetails
          itemKey={props.item.key}
          locationKey={props.entry.location.key}
          expanded={isLotExpanded}
        />
      )}
    </Fragment>
  );
}

/**
 * Renders one item's stock at one site, either as a single row or as a zone breakdown
 */
function InventorySiteRows(props: {
  label: ReactNode;
  item: SparseItem;
  site: SparseLocation;
  inventory: InventoryEntry[];
  expandedZones: Record<string, boolean>;
  onToggleZoneExpand: (zoneKey: string) => void;
  expandedLots: Record<string, boolean>;
  onToggleLotExpand: (lotKey: string) => void;
}) {
  const totalCount = getTotalCount(props.inventory);
  const expandKey = getLotKey(props.item.key, props.site.key);

  if (!hasZoneBreakdown(props.site, props.inventory)) {
    const isLotExpanded = props.expandedLots[expandKey] ?? false;

    return (
      <Fragment>
        <TableRow>
          <TableCell width="55%" sx={{ paddingLeft: 6 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ExpandToggle
                isExpanded={isLotExpanded}
                noun="lots"
                onToggle={() => props.onToggleLotExpand(expandKey)}
              />
              <Typography variant="body2">{props.label}</Typography>
            </Stack>
          </TableCell>
          <TableCell width="20%" align="right">
            <Typography variant="body2">{formatNumber(totalCount)}</Typography>
          </TableCell>
          <TableCell width="25%" align="right">
            <InventoryChildRowActions
              item={props.item}
              location={props.site}
              quantity={totalCount}
            />
          </TableCell>
        </TableRow>
        <InventoryLotDetails
          itemKey={props.item.key}
          locationKey={props.site.key}
          expanded={isLotExpanded}
        />
      </Fragment>
    );
  }

  const isZoneExpanded = props.expandedZones[expandKey] ?? false;

  return (
    <Fragment>
      <TableRow>
        <TableCell width="55%" sx={{ paddingLeft: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ExpandToggle
              isExpanded={isZoneExpanded}
              noun="zones"
              onToggle={() => props.onToggleZoneExpand(expandKey)}
            />
            <Typography variant="body2">{props.label}</Typography>
          </Stack>
        </TableCell>
        <TableCell width="20%" align="right">
          <Typography variant="body2">{formatNumber(totalCount)}</Typography>
        </TableCell>
        <TableCell width="25%" />
      </TableRow>

      {isZoneExpanded &&
        props.inventory.map((entry) => (
          <ZoneSubRow
            key={entry.location.id}
            entry={entry}
            site={props.site}
            item={props.item}
            expandedLots={props.expandedLots}
            onToggleLotExpand={props.onToggleLotExpand}
          />
        ))}
    </Fragment>
  );
}

export function InventoryChildRow(props: InventoryChildRowProps) {
  const itemRow = props.itemRow;
  if (!props.expanded) {
    return null;
  }

  return (
    <TableRow>
      <TableCell colSpan={3} sx={{ padding: 0, borderBottom: 'none' }}>
        <Collapse in={props.expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              bgcolor: 'action.hover',
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableBody>
                <TableRow>
                  <TableCell width="55%" sx={{ paddingLeft: 8 }}>
                    <Typography variant="subtitle2">
                      {props.view === 'item' ? 'Locations' : 'Items'}
                    </Typography>
                  </TableCell>
                  <TableCell width="20%" />
                  <TableCell width="25%" />
                </TableRow>

                {props.view === 'item' &&
                  itemRow?.locations.map((row) => (
                    <InventorySiteRows
                      key={row.location.key}
                      label={getEffectiveLocationName(row.location)}
                      item={itemRow.item}
                      site={row.location}
                      inventory={row.inventory}
                      expandedZones={props.expandedZones}
                      onToggleZoneExpand={props.onToggleZoneExpand}
                      expandedLots={props.expandedLots}
                      onToggleLotExpand={props.onToggleLotExpand}
                    />
                  ))}

                {props.view === 'location' &&
                  props.locationRow?.items?.bulkItems?.map((row) => (
                    <InventorySiteRows
                      key={row.item.key}
                      label={
                        <>
                          {row.item.sku}
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            {' '}
                            ({row.item.name})
                          </Typography>
                        </>
                      }
                      item={row.item}
                      site={row.location}
                      inventory={row.inventory}
                      expandedZones={props.expandedZones}
                      onToggleZoneExpand={props.onToggleZoneExpand}
                      expandedLots={props.expandedLots}
                      onToggleLotExpand={props.onToggleLotExpand}
                    />
                  ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}
